import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { VaccineSchedule } from "../../interfaces/vaccines";
import { getVaccineSchedules } from "../../apis/vaccines";
import MastersBreadcrumb from "../../components/MastersBreadcrumb";

const unwrapList = (res: any): VaccineSchedule[] => {
  const data = res?.data ?? res;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
};

const formatAge = (s: VaccineSchedule) => {
  const parts: string[] = [];
  if (s.recommendedAgeYears) parts.push(`${s.recommendedAgeYears}y`);
  if (s.recommendedAgeMonths) parts.push(`${s.recommendedAgeMonths}m`);
  if (s.recommendedAgeDays) parts.push(`${s.recommendedAgeDays}d`);
  return parts.length ? parts.join(" ") : "-";
};

const VaccineSchedulesList: React.FC = () => {
  const [schedules, setSchedules] = useState<VaccineSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async (q?: string) => {
    setLoading(true);
    try {
      const res: any = await getVaccineSchedules({
        per_page: 1000,
        ...(q ? { search: q } : {}),
      });
      setSchedules(unwrapList(res));
    } catch (error) {
      console.log("Error fetching vaccine schedules:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <MastersBreadcrumb current="Vaccine Schedules" />
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Vaccine Schedules
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            All dose schedules across vaccines. Edit via the Vaccines master.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/vaccines")}
          className="px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover"
        >
          Manage Vaccines
        </button>
      </div>

      <div className="mt-4">
        <input
          type="search"
          placeholder="Search by vaccine name..."
          className="block w-full max-w-md rounded-md border-gray-300 shadow-sm sm:text-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") fetchSchedules(search.trim());
          }}
        />
      </div>

      <div className="mt-6 overflow-x-auto -mx-4 ring-1 ring-gray-300 sm:mx-0 sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-3.5 pr-3 pl-4 text-sm font-semibold text-left text-gray-900 sm:pl-6">
                Vaccine
              </th>
              <th className="px-3 py-3.5 text-sm font-semibold text-left text-gray-900">
                Dose
              </th>
              <th className="px-3 py-3.5 text-sm font-semibold text-left text-gray-900">
                Recommended Age
              </th>
              <th className="px-3 py-3.5 text-sm font-semibold text-left text-gray-900">
                Min Interval
              </th>
              <th className="px-3 py-3.5 text-sm font-semibold text-left text-gray-900">
                Booster
              </th>
              <th className="px-3 py-3.5 text-sm font-semibold text-left text-gray-900">
                Notes
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-sm text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : schedules.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-sm text-center text-gray-500">
                  No schedules found.
                </td>
              </tr>
            ) : (
              schedules.map((s) => (
                <tr key={s.id}>
                  <td className="py-4 pr-3 pl-4 text-sm font-medium text-gray-900 sm:pl-6">
                    {s.vaccine?.name || "-"}
                    {s.vaccine?.shortName ? (
                      <span className="ml-1 text-gray-500">
                        ({s.vaccine.shortName})
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-700">
                    Dose {s.doseNumber}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    {formatAge(s)}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    {s.minimumIntervalDays != null
                      ? `${s.minimumIntervalDays} days`
                      : "-"}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    {s.isBooster ? "Yes" : "No"}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    {s.notes || "-"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VaccineSchedulesList;
